/**
 * 작성자 : 김광민
 * 날짜 : 2024.04.01
 * 기능 : PTZ 속도와 화면분할, 캡쳐파일 저장 위치, 동영상 파일 저장 위치를 설정하는 컴포넌트
 */

import styled from 'styled-components';
import { IModal } from 'customTypes';
import { Dispatch, SetStateAction, useState } from 'react';
import { WebSDKcctv } from '../../utils/webSDKcctvClass';
import SelectOptionsButtons from './CctvSettings/SelectOptionsButtons';
import SetDownloadPath from './CctvSettings/SetDownloadPath';
import { initPath } from '../../services/webSDK';
import { IsInfraType } from '../../pages/s_cctv/S_cctv1/RealtimeCCTV/IsInfraType';
import { apiPatch } from '../../services/_common';
import { useRecoilValue, useRecoilState } from 'recoil';
import { localCctvSettingState, userState } from '../../atoms';
import CameraChannelView from '../CameraChannelView';
import CameraFontPosition from '../CameraFontPosition';
import CameraFontColors from '../CameraFontColors';
import { useTranslation } from 'react-i18next';
import { logPost } from '../../services/log';

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5010;
  width: 100%;
  height: 100%;
  height: 100vh;
  height: 100dvh;
  background-color: rgba(0, 0, 0, 60%);
  backdrop-filter: blur(0.5rem);
  transition: all ease 0.8s;
  display: flex;
  justify-content: center;
  align-items: center;
  > .cctvSettingsModal {
    position: relative;
    z-index: 6000;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 2rem);
    max-height: calc(100dvh - 2rem);
    width: 32rem;
    background-color: var(--board);
    border-radius: 0.5rem;
    overflow: hidden;
    user-select: none;
    @media screen and (max-width: 1024px) {
      width: calc(100vw - 2rem);
    }
    @media screen and (max-width: 767px) {
      width: 100%;
      height: 100vh;
      height: inherit;
      overflow-y: auto;
      border-radius: 0;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: var(--text-secondary);
      background-color: var(--tonal);
      border-bottom: 1px solid var(--outline);
      padding: 0.5rem;
      > .titleName {
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.5rem;
      }
      > .closeButton {
        border-radius: 0.5rem;
        margin: 0.25rem;
        padding: 0.25rem;
        display: flex;
        cursor: pointer;
        color: var(--text-disabled);
        &:hover {
          color: var(--text-tertiary);
          background-color: var(--tonal-deep);
          box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.2);
        }
      }
    }
    .body {
      flex-grow: 1;
      padding: 1rem;
      padding-bottom: 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }
    .footer {
      display: flex;
      justify-content: flex-end;
      background-color: var(--board);
      border-top: 1px solid var(--outline);
      padding: 0.5rem;
      > .button {
        display: flex;
        align-items: center;
        height: 2.5rem;
        min-width: 5.5rem;
        justify-content: center;
        border-radius: 0.25rem;
        font-size: 0.875rem;
        letter-spacing: normal;
        background-color: var(--tonal-deep);
        color: var(--text-tertiary);
        padding: 0 0.5rem;
        cursor: pointer;
        background-color: var(--primary);
        color: var(--text-reverse);
        &:active {
          background-color: var(--filled-blue-deep);
        }
        &.disabled {
          pointer-events: none;
          color: var(--text-disabled);
          background-color: var(--tonal-deep);
          background-color: var(--tonal);
        }
      }
    }
  }
  > .confirmModal {
    position: relative;
    z-index: 6000;
    display: flex;
    flex-direction: column;
    height: 10rem;
    max-height: calc(100vh - 2rem);
    max-height: calc(100dvh - 2rem);
    width: 24rem;
    background-color: var(--board);
    border-radius: 0.5rem;
    overflow: hidden;
    user-select: none;
    .title {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 6rem;
      flex-shrink: 0;
      border-bottom: 1px solid var(--outline);
      .main {
        color: var(--text-tertiary);
      }
      .sub {
        font-weight: 600;
        color: var(--text-secondary);
      }
    }
    .buttons {
      flex-grow: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0.5em;
      padding: 0.5rem 0.75rem;
      background-color: var(--tonal);
      .button {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 3rem;
        height: 100%;
        width: calc(50% - 0.25rem);
        border-radius: 0.25rem;
        gap: 0.5rem;
        cursor: pointer;
        color: var(--text-tertiary);
        &:hover {
          background-color: var(--board);
          color: var(--text-secondary);
          box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.05);
          &:active {
            background-color: var(--tonal-deep);
            color: var(--text-secondary);
            box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.1);
          }
        }
      }
    }
  }
`;

const SelectWrapButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  & > .label {
    font-size: 0.875rem;
    color: rgb(82, 82, 91);
    display: flex;
    -webkit-box-align: center;
    align-items: center;
    gap: 0.25rem;
    height: 2rem;

    .material-symbols-rounded {
      font-variation-settings: 'FILL' 0, 'wght' 300;
      font-size: 1.5rem;
      color: var(--text-tertiary);
    }
  }
`;

type Props = {
  setOpenModal: Dispatch<SetStateAction<{ status: boolean; type: string }>>; //
  cctvOBJ: WebSDKcctv;
  cctvSettings: CctvSettings;
  setSliderValue?: Dispatch<SetStateAction<number>>;
  isPlaycctv?: boolean;
};

const CctvSettings = ({ setOpenModal, cctvOBJ, cctvSettings, setSliderValue, isPlaycctv = false }: Props) => {
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const { userId } = userInfo;
  const [localCctvSettings, setLocalCctvSettings] = useRecoilState(localCctvSettingState);
  const initialFontLocation = localCctvSettings.camCLocation || cctvSettings.camCLocation;
  const [fontLocation, setFontLocation] = useState({ camCLocation: initialFontLocation }); // 카메라명 폰트 위치
  const isInfraType = IsInfraType(); // 인프라용의 경우 다운로드 폴더 미노출
  const initialFontColor = localCctvSettings.camFColor || cctvSettings.camFColor;
  const [fontColor, setFontColor] = useState({ camFColor: initialFontColor }); // 카메라명 폰트 색상
  const initialPtzSpeed = localCctvSettings.camSpeed || cctvSettings.camSpeed;
  const [ptzSpeed, setPtzSpeed] = useState(initialPtzSpeed); // PTZ 속도
  // PTZ 속도 Options 생성
  const ptzSpeedOptions = Array.from({ length: 7 }, (_, index) => {
    const indexKey = index + 1;
    const key = indexKey.toString();
    return {
      key,
      name: key,
    };
  });

  const initialGridView = localCctvSettings.camCType || cctvSettings.camCType;
  const [gridView, setGridView] = useState({ camCType: initialGridView }); // 화면 분할

  // 저장 버튼 클릭 후 확인 모달 유무
  const [isShowConfirmModal, setIsShowConfirmModal] = useState<boolean>(false);

  // 캡쳐 파일 저장 위치
  const [captureDownloadPath, setCaptureDownloadPath] = useState<string | null>(initPath(localCctvSettings.camCPath || cctvSettings.camCPath));
  // 동영상 파일 저장 위치
  const [videoDownloadPath, setVideoDownloadPath] = useState<string | null>(initPath(localCctvSettings.camMPath || cctvSettings.camMPath));

  // 모달 닫기
  const onClickClose = () => {
    setOpenModal((prev: IModal) => ({ ...prev, status: false }));
  };

  // 다운로드 경로 JSON 문자열 변환 함수
  const setJsonPath = (path: string | null) => {
    if (path === 'undefined' || path === undefined || path === null || path === '') {
      return '';
    }
    return JSON.stringify(path);
  };

  const getCamListValue = (camList: any) => {
    if (!camList || camList === 'undefined') return '';
    return typeof camList === 'string' ? camList : JSON.stringify(camList);
  };

  const onClickSave = async () => {
    const captureDownload = setJsonPath(captureDownloadPath);
    const videoDownload = setJsonPath(videoDownloadPath);

    const req = {
      ...cctvSettings,
      // 화면 분할 1*1(1) | 2*2(4) | 3*3(9) | 4*4(16)
      camCType: gridView.camCType.toString() as '1' | '2' | '3' | '4',
      // 카메라명 폰트 위치
      camCLocation: fontLocation.camCLocation,
      // 카메라명 폰트 색상
      camFColor: fontColor.camFColor,
      // PTZ 카메라 이동 속도
      camSpeed: ptzSpeed.toString() as '1' | '2' | '3' | '4' | '5' | '6' | '7',
      // 캡쳐 파일 저장 경로
      camCPath: captureDownload,
      // 동영상 파일 저장 경로
      camMPath: videoDownload,
      camList1: '',
      camList2: '',
      camList3: '',
      camList4: '',
      camList5: '',
      userId,
    };

    await apiPatch({ path: '/user/cctv', req }).then(async () => {
      setLocalCctvSettings(req);
      if (gridView.camCType && !isPlaycctv) {
        cctvOBJ.changeWndNum(Number(gridView.camCType));
      }
      if (ptzSpeed && setSliderValue && !isPlaycctv) {
        setSliderValue(Number(ptzSpeed));
      }
      if ((fontColor.camFColor || fontLocation.camCLocation) && !isPlaycctv) {
        cctvOBJ?.setSnapPolygon({ camFColor: fontColor.camFColor, camCLocation: fontLocation.camCLocation });
      }
      if (captureDownloadPath || videoDownloadPath) {
        // webSDK 다운로드 경로 저장
        cctvOBJ.setLocalCfg({
          capturePath: captureDownloadPath, // 캡쳐 파일 저장 경로
          videoPath: videoDownloadPath, // 동영상 파일 저장 경로
        });
      }
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: 'CCTV 환경설정',
        action: '저장',
        etc: `카메라 설정 저장`,
      });
    });
    onClickClose();
  };

  // 변경사항 유무에 따라 저장 버튼 활성/비활성
  const isDisableSave = () => {
    const isFontLocation = (localCctvSettings.camCLocation || cctvSettings.camCLocation) === fontLocation.camCLocation;
    const isFontColor = (localCctvSettings.camFColor || cctvSettings.camFColor) === fontColor.camFColor;
    const isPtzSpeed = (localCctvSettings.camSpeed || cctvSettings.camSpeed) === ptzSpeed;
    const isGridView = (localCctvSettings.camCType || cctvSettings.camCType) === gridView.camCType;
    const isCaptureDownloadPath = initPath(localCctvSettings.camCPath || cctvSettings.camCPath) === captureDownloadPath;
    const isVideoDownloadPath = initPath(localCctvSettings.camMPath || cctvSettings.camMPath) === videoDownloadPath;
    return isFontLocation && isFontColor && isPtzSpeed && isGridView && isCaptureDownloadPath && isVideoDownloadPath;
  };

  return (
    <Root onClick={onClickClose} role='presentation'>
      <div
        className='cctvSettingsModal'
        role='presentation'
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className='header'>
          <div className='titleName'>{t('CCTV 환경설정')}</div>
          <span
            className='material-symbols-rounded closeButton' //
            onClick={onClickClose}
            role='presentation'
          >
            close
          </span>
        </div>
        <div className='body'>
          {!isPlaycctv && (
            <>
              <SelectOptionsButtons
                label='카메라 PTZ 속도'
                state={ptzSpeed} //
                setState={setPtzSpeed}
                options={ptzSpeedOptions}
                icon='speed'
              />

              <SelectWrapButtons>
                <span className='label'>
                  <span className='material-symbols-rounded'>grid_view</span>
                  {t('화면 분할')}
                </span>
                <CameraChannelView camCType={gridView.camCType} setState={setGridView} minWidth />
              </SelectWrapButtons>

              <SelectWrapButtons>
                <span className='label'>
                  <span className='material-symbols-rounded'>picture_in_picture_alt</span>
                  {t('카메라명 폰트 위치')}
                </span>
                <CameraFontPosition camCLocation={fontLocation.camCLocation} setState={setFontLocation} minWidth />
              </SelectWrapButtons>

              <SelectWrapButtons>
                <span className='label'>
                  <span className='material-symbols-rounded'>palette</span>
                  {t('카메라명 폰트 색상')}
                </span>
                <CameraFontColors camFColor={fontColor.camFColor} setState={setFontColor} minWidth />
              </SelectWrapButtons>
            </>
          )}

          {!isInfraType && (
            <>
              <SetDownloadPath
                label='캡쳐 다운로드 폴더 경로'
                icon='photo_camera' //
                onClick={() => cctvOBJ.downloadPathSetting(setCaptureDownloadPath)}
                path={captureDownloadPath}
              />
              <SetDownloadPath
                label='동영상 다운로드 폴더 경로'
                icon='videocam' //
                onClick={() => cctvOBJ.downloadPathSetting(setVideoDownloadPath)}
                path={videoDownloadPath}
              />
            </>
          )}
        </div>

        <div className='footer'>
          <div
            className={`button ${isDisableSave() ? 'disabled' : ''}`} //
            onClick={onClickSave}
            role='presentation'
          >
            {t('저장')}
          </div>
        </div>
      </div>
      {/* {!isShowConfirmModal ? (
      ) : (
        <div
          className='confirmModal'
          role='presentation'
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className='title'>
            <div className='main'>CCTV 환결설정이 성공적으로 저장되었습니다.</div>
            <div className='sub'>계속해서 수정하시겠습니까?</div>
          </div>
          <div className='buttons'>
            <span
              className='button button_cancel' //
              role='presentation'
              onClick={onClickClose}
            >
              아니요
            </span>
            <span
              className='button button_confirm' //
              role='presentation'
              onClick={() => setIsShowConfirmModal(false)}
            >
              예
            </span>
          </div>
        </div>
      )} */}
    </Root>
  );
};

export default CctvSettings;
